// Set ağacı satırının taşma menüsü ⋯ (spec §6.3 DR15 — "+ Kırılım grubu ekle" / "+ Üye" satırda
// görünür düğme değil, burada). ARIA menü düğmesi: aria-haspopup/aria-expanded, role=menu +
// menuitem; Esc / dışarı tıklama kapatır, Esc set ağacına (modal kapanışına) yayılmaz.

import { useEffect, useId, useRef, useState } from "react";
import { KPI_BREAKDOWN_COPY } from "../../lib/kpi-breakdown-copy";
import { setRowMenuKey } from "../../lib/set-row-menu-keys";
import { btnSmall } from "./kpi-form-styles";

export interface SetRowMenuItem {
  key: string;
  label: string;
  onSelect: () => void;
  tone?: "danger";
}

export function SetRowMenu(props: {
  label: string;
  items: readonly SetRowMenuItem[];
}): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLSpanElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent): void {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    rootRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // TODO-KPI-120: menü kapanınca odak tetikleyiciye döner; ok tuşları öğeler arası gezer
  function closeMenu(): void {
    setOpen(false);
    triggerRef.current?.focus();
  }
  function menuItems(): HTMLElement[] {
    return [...(rootRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])];
  }

  if (props.items.length === 0) return null;
  return (
    <span
      ref={rootRef}
      style={{ position: "relative", display: "inline-block" }}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (!open) return;
        const items = menuItems();
        const at = items.indexOf(document.activeElement as HTMLElement);
        const act = setRowMenuKey(e.key, at, items.length);
        // "activate": Enter/Boşluk odaktaki düğmenin yerel tıklamasına bırakılır (çift tetik yok)
        if (act === null || act === "activate") return;
        e.preventDefault();
        if (act === "close") closeMenu();
        else items[act]?.focus();
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-label={KPI_BREAKDOWN_COPY.tree.menu(props.label)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        data-testid="kpi-set-row-menu"
        style={{ ...btnSmall, padding: "2px 10px", minWidth: 32 }}
        onClick={() => setOpen((v) => !v)}
      >
        {KPI_BREAKDOWN_COPY.tree.menuButton}
      </button>
      {open ? (
        <span
          id={menuId}
          role="menu"
          className="rounded-lg"
          style={{
            position: "absolute",
            right: 0,
            top: "calc(100% + 4px)",
            zIndex: 20,
            display: "grid",
            minWidth: 200,
            background: "var(--color-surface)",
            border: "1px solid var(--color-border)",
            padding: 4,
          }}
        >
          {props.items.map((it) => (
            <button
              key={it.key}
              type="button"
              role="menuitem"
              tabIndex={-1}
              onClick={() => {
                // odak önce tetikleyiciye döner: eylem pencere açarsa KpiModal oradan geri odaklar
                closeMenu();
                it.onSelect();
              }}
              style={{
                textAlign: "left",
                border: 0,
                background: "transparent",
                padding: "8px 10px",
                minHeight: 40,
                borderRadius: 6,
                cursor: "pointer",
                fontSize: "0.875rem",
                color: it.tone === "danger" ? "var(--color-error)" : "var(--color-text)",
              }}
            >
              {it.label}
            </button>
          ))}
        </span>
      ) : null}
    </span>
  );
}
